/**
 * Widget editor dialog.
 * Allows editing basic widget metadata and raw JSON configuration.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import type { DashboardWidgetDto, UpdateDashboardWidgetRequest } from '../../types/dashboard';

export interface WidgetEditorDialogProps {
  /**
   * Whether the dialog is open.
   */
  open: boolean;

  /**
   * The widget being edited.
   */
  widget: DashboardWidgetDto | null;

  /**
   * Called when the dialog should be closed.
   */
  onClose: () => void;

  /**
   * Called when the user saves changes.
   */
  onSave: (widgetId: string, request: UpdateDashboardWidgetRequest) => Promise<void> | void;
}

/**
 * Widget editor dialog component.
 */
export const WidgetEditorDialog = ({ open, widget, onClose, onSave }: WidgetEditorDialogProps) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [widgetType, setWidgetType] = useState('');
  const [configurationJson, setConfigurationJson] = useState('{}');
  const [isVisible, setIsVisible] = useState(true);

  const baseline = useMemo(() => widget, [widget]);

  useEffect(() => {
    if (!widget) return;
    setTitle(widget.title ?? '');
    setDescription(widget.description ?? '');
    setWidgetType(widget.widgetType ?? '');
    setConfigurationJson(widget.configurationJson ?? '{}');
    setIsVisible(widget.isVisible ?? true);
  }, [widget]);

  if (!baseline) return null;

  const buildRequest = (): UpdateDashboardWidgetRequest => {
    return {
      widgetType: widgetType.trim(),
      title: title.trim(),
      description: description.trim() || undefined,
      configurationJson: configurationJson?.trim() || '{}',
      positionX: baseline.positionX,
      positionY: baseline.positionY,
      width: baseline.width,
      height: baseline.height,
      isVisible,
      sortOrder: baseline.sortOrder,
    };
  };

  const handleSave = async () => {
    if (!baseline) return;
    await onSave(baseline.id, buildRequest());
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Edit Widget</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'grid', gap: 2, mt: 1 }}>
          <TextField
            label="Widget Type"
            value={widgetType}
            onChange={(e) => setWidgetType(e.target.value)}
            helperText="Used for rendering in the dashboard."
          />

          <TextField label="Title" value={title} onChange={(e) => setTitle(e.target.value)} />

          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            minRows={2}
          />

          <FormControlLabel
            control={<Switch checked={isVisible} onChange={(e) => setIsVisible(e.target.checked)} />}
            label="Visible"
          />

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Configuration JSON
            </Typography>
            <TextField
              value={configurationJson}
              onChange={(e) => setConfigurationJson(e.target.value)}
              multiline
              minRows={8}
              fullWidth
              helperText="Stored as raw JSON. You can use this to configure queries, chart options, thresholds, etc."
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={!title.trim() || !widgetType.trim()}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

